import React, { useState } from 'react';
import { LockClosedIcon, PencilSquareIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ordersService, {
  type OrderDetails,
  type AddlCostDriverPayItem,
  type AddlCostItem,
  type RejectedDriverPaperworkItem,
} from '../../../services/orders.service';
import { Row, Label, Section, Icon, TextInput, SelectInput } from './orderform';
import BulkFieldCheckbox from '../changeAll/BulkFieldCheckbox';
import DateTimePicker from '../../../components/DateTimePicker';
import { formatMdYDate } from '../../../utils/dateUtils';
import { useCanEditOrderLcFields } from '../useCanEditOrderLcFields';
import { isTemplatePronumber } from '../templateFieldPolicy';
import DriverPayAdditionModal, {
  DRIVER_PAY_PERMISSION_ID,
  DRIVER_PAY_PERMISSION_NAME,
} from '../../../components/pro/DriverPayAdditionModal';
import AdditionalCostItemModal, {
  COST_ITEM_PERMISSION_ID,
  COST_ITEM_PERMISSION_NAME,
} from '../../../components/pro/AdditionalCostItemModal';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { PermissionAwareButton } from '../../../contexts/PermissionAccessContext';
import { gwiCommissionFor, formatMoney } from '../../../utils/gwiCommission';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';

/**
 * Read-only display of the V1 "Addl Cost" tab. The layout mirrors:
 *   - left column: `@edit_layout_addlcost` (cms-master/lib/defs.pl:2631-2637)
 *   - right side:  the two line-item tables built by `drawtab_AddlCost`
 *                  (cms-master/lib/lib_editrecord.pl:1987-2110), which call
 *                  `getDriverPayLI` and `getAddlCostItems` for the rows.
 *
 * Per-diem / chassis fields, both line-item tables, and the Total GWI
 * Payment summary come from the orders endpoint. Rejected Driver Paperwork
 * remains a stub until the legdocumentinfo query is wired.
 */
export interface AddlCostViewProps {
  details: Partial<OrderDetails> | null;
  /** Pro number — required to open the driver-pay addition modal. When
   *  omitted, Add / row-click affordances are hidden (read-only mode). A
   *  "T"-prefixed template pronumber (string) is always read-only: templates
   *  have no additional-cost / driver-pay rows and cannot be edited. */
  pronumber?: number | string;
  /** Called after a successful create / update / delete in the modal so the
   *  parent can re-fetch order details and refresh the table. May return a
   *  Promise; the modal awaits it before closing. */
  onRefresh?: () => void | Promise<void>;
  /** Inline-edit surface (subset of useOrderEditing). When present on a real
   *  order, the LC-editable per-diem fields render as inputs. */
  editing?: {
    editedOrderFields: Record<string, any>;
    updateOrderField: (name: string, value: any) => void;
    /** Drops the pending edit for a field so it falls back to the loaded value.
     *  Used by the Responsibility prompt's Cancel (V1 reset the dropdown to
     *  `oldorder_perdiemapproval`, i.e. the value the record was loaded with). */
    revertOrderField?: (name: string) => void;
  };
  /** Called with the note text after the Per Diem Responsibility prompt saves it.
   *  Lets the host stage the same answer for a Change All fan-out, so one Apply
   *  carries both the Responsible column and this note (V1 had no fan-out; the
   *  note is per-pro data the anchor's prompt collected). */
  onPerDiemNoteSaved?: (note: string) => void;
}

// V1 perdiemapproval_data (defs.pl). Leading '' = the unselected row.
const PERDIEM_APPROVAL_OPTIONS = [
  '',
  'GULF WINDS',
  'CUSTOMER',
  'CUSTOMER REVIEW',
  'DISPUTE',
  'GWI/CUSTOMER',
  'SSLINE WAIVED',
  'IMPORT CUSTOMER',
];

const s = (v: unknown): string => (v == null ? '' : String(v));

const PLACEHOLDER = '—';
const LABEL_W = 144;

interface ReadValueProps {
  value?: unknown;
  mono?: boolean;
  width?: number;
  ariaLabel?: string;
  /** 'red' = red pill (unbillable fee); 'white' = white text, transparent bg
   *  (a value sitting on the red past-due per-diem row). */
  tone?: 'red' | 'white';
}
const ReadValue: React.FC<ReadValueProps> = ({ value, mono, width, ariaLabel, tone }) => {
  const text = value == null || value === '' ? PLACEHOLDER : String(value);
  const isEmpty = text === PLACEHOLDER;
  const toneClass =
    tone === 'red' ? 'bg-brand-red text-white rounded' : tone === 'white' ? 'text-white' : '';
  const cls = [
    'inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate',
    '',
    toneClass || (isEmpty ? 'text-slate-400 dark:text-gray-500' : 'text-slate-900 dark:text-gray-100'),
    width ? '' : 'flex-1 min-w-0',
  ]
    .filter(Boolean)
    .join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  return (
    <span className={cls} style={style} aria-label={ariaLabel} title={isEmpty ? undefined : text}>
      {text}
    </span>
  );
};

const MS_PER_DAY = 86_400_000;

/**
 * Parse a backend-formatted per-diem date ("MM/dd" for the current year or
 * "MM/dd/yy" otherwise) into a midnight Date, or null when absent/unparseable.
 */
function parsePerDiemDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(String(value).trim());
  if (!m) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let year: number;
  if (m[3]) {
    const yy = parseInt(m[3], 10);
    year = yy < 100 ? 2000 + yy : yy;
  } else {
    year = today.getFullYear();
  }
  const parsed = new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

/** True when the per-diem start date is strictly before today (past due). */
function isPerDiemPastDue(value: unknown): boolean {
  const start = parsePerDiemDate(value);
  if (!start) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return start.getTime() < today.getTime();
}

/**
 * Whole days of per diem accrued so far — from the start date up to the end
 * date when one is set, otherwise up to today. Mirrors V1
 * `lib_editrecord.pl:263-354` (`enddays - startdays`, falling back to now()
 * when no end date). Returns null when there's no start date or the span is
 * non-positive, matching the legacy `enddays > startdays` guard.
 */
function perDiemDaysAccrued(start: unknown, end: unknown, fallbackEnd?: unknown): number | null {
  const startDate = parsePerDiemDate(start);
  if (!startDate) return null;
  // V1 enddays: perdiemenddate, else the terminate/delivery leg arrival
  // (fallbackEnd), else today.
  let endDate = parsePerDiemDate(end) ?? parsePerDiemDate(fallbackEnd);
  if (!endDate) {
    endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
  }
  const days = Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY);
  return days > 0 ? days : null;
}

const Divider: React.FC = () => (
  <div className="my-1.5 border-t border-dashed border-slate-200 dark:border-gray-700" />
);

const TH: React.FC<{ children?: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <th
    scope="col"
    className={
      'text-left text-[0.65625rem] font-semibold uppercase tracking-wider ' +
      'text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700 whitespace-nowrap ' +
      (className ?? '')
    }
  >
    {children}
  </th>
);

const TD: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td
    className={
      'px-2 py-1 text-[0.75rem] text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700 whitespace-nowrap ' +
      (className ?? '')
    }
  >
    {children}
  </td>
);

const money = (n: number | null | undefined): string =>
  n == null ? PLACEHOLDER : formatMoney(n);

/** #33649 — a BOBTAIL whose zone lookup matched no driverpay_rates row. It is
 *  the only addition type here that resolves through the rate engine, so it's
 *  the only one where a missing rate row is a defect: every other type is
 *  hand-rated and carries raterecno=0 normally. Note this is NOT "rate is
 *  0.00" — it's the engine finding nothing for the lane. Same state
 *  margin_service reports as MISSING RATE (V1:13030). An ABSENT value is
 *  deliberately not a match: a payload that predates the column is unknown,
 *  not missing, and a false badge on every Bobtail is worse than none.
 *
 *  #33741 — reads `effectiveRateRecno`, which is the stored column falling back
 *  to a live lookup. The STORED column lies on every Bobtail this page created
 *  before #33649 taught it to persist the value, and a paid row can never be
 *  saved again to heal itself, so trusting it badged settled lines forever. */
const isMissingBobtailRate = (row: AddlCostDriverPayItem): boolean =>
  row.type === 'BOBTAIL' && row.effectiveRateRecno === 0;

const MISSING_RATE_TITLE =
  'No Bobtail rate is on file for this lane — the line cannot be approved for PAY '
  + 'until the rate is added in Driver Pay Rates.';

/** V1 legtype_abbr mapping from cms-master/lib/defs.pl:4536-4551. */
const LEG_TYPE_ABBR: Record<string, string> = {
  'ADD PRODUCT': 'ADD',
  BOBTAIL: 'B',
  'CHASSIS SPLIT': 'CS',
  GROUNDED: 'GR',
  DELIVERY: 'D',
  PICKUP: 'PU',
  REJECTED: 'RJ',
  'REMOVE PRODUCT': 'REM',
  REWORK: 'RW',
  SCALE: 'S',
  STOPOFF: 'SO',
  STORAGE: 'ST',
  TERMINATE: 'T',
  TRANSLOAD: 'TR',
};

/** Render a leg-type abbreviation chevron with CHASSIS SPLIT in red. */
const LegTypeChevron: React.FC<{ value: string; ariaLabel: string }> = ({
  value,
  ariaLabel,
}) => {
  const abbr = LEG_TYPE_ABBR[value] ?? value;
  const isChassisSplit = value === 'CHASSIS SPLIT';
  return (
    <span
      className={
        'inline-flex items-center justify-center text-[0.6875rem] ' +
        (isChassisSplit ? 'text-red-600 dark:text-red-300 font-semibold' : 'text-slate-500 dark:text-gray-400')
      }
      aria-label={ariaLabel}
      title={value || undefined}
    >
      {abbr || PLACEHOLDER}
    </span>
  );
};

/** Mirror V1's transformation `s/([A-Z])/ $1/g` then uc — camelCase splits at
 * each uppercase letter with a leading space, then the whole string is
 * uppercased. So `inInterchange` becomes `IN INTERCHANGE`. */
const formatInfoType = (s: string): string =>
  s.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

/** Dispatcher usernames whose orders surface the Brokerage carrier block.
 *  Matched case-insensitively against the order's dispatcher, so reassigning
 *  the order to/from one of these dispatchers shows/hides the block on refresh. */
const BROKERAGE_DISPATCHERS = new Set(['highway', 'dallasdomestic', 'brokerage']);

const AddlCostView: React.FC<AddlCostViewProps> = ({
  details,
  pronumber,
  onRefresh,
  editing,
  onPerDiemNoteSaved,
}) => {
  const d = details ?? {};

  // Inline-edit surface for the LC-editable per-diem fields. Templates and the
  // read-only view (no `editing`) stay display-only, same gate as the other tabs.
  const canEditOrderLcFields = useCanEditOrderLcFields();
  const isTemplate = isTemplatePronumber(d.pronumber ?? pronumber);
  const ef = editing?.editedOrderFields ?? {};
  const upd = editing?.updateOrderField;
  const perDiemEditable = !isTemplate && !!editing && canEditOrderLcFields;
  /** Effective value: edited wins, then details. */
  const fv = (key: string): unknown => (ef[key] !== undefined ? ef[key] : (d as any)[key]);

  // Per Diem Responsibility note modal (v1 perdieminfo). Two ways in, and Cancel
  // means something different in each:
  //   'pencil' — the icon next to Responsible. Cancel just closes.
  //   'prompt' — auto-opened when Responsible is SET to a non-blank value, V1's
  //     onchange on the dropdown (defs.pl:494). Cancel reverts the dropdown to the
  //     loaded value, exactly as V1's `edit_setvalue(... oldorder_perdiemapproval)`.
  // Save posts to /orders/perdiem-notes immediately in both — V1's prompt Save
  // posted saveperdieminfo on its own, independent of the record save — then
  // re-fetches so the panel shows the stored note.
  const [notesMode, setNotesMode] = useState<'pencil' | 'prompt' | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const {
    panelRef: notesPanelRef,
    panelStyle: notesPanelStyle,
    dragHandleProps: notesDragHandleProps,
  } = useModalDrag(notesMode !== null);
  const openNotes = (mode: 'pencil' | 'prompt') => {
    setNotesDraft(s(d.perDiemNotes));
    setNotesMode(mode);
  };
  // V1 prompts only when the new value is non-blank: BLANKING Responsible clears the
  // field without asking (defs.pl:494 `if(this.value!='')`).
  const handleResponsibleChange = (v: string) => {
    upd?.('perDiemApproval', v);
    if (v !== '') openNotes('prompt');
  };
  const cancelNotes = () => {
    if (notesMode === 'prompt') editing?.revertOrderField?.('perDiemApproval');
    setNotesMode(null);
  };
  const saveNotes = async () => {
    if (pronumber == null) return;
    setNotesSaving(true);
    try {
      await ordersService.savePerDiemNotes(pronumber, notesDraft);
      setNotesMode(null);
      // Staged for a Change All fan-out, so the same answer reaches every target pro
      // alongside the Responsible column. Trimmed to match what the server stored.
      onPerDiemNoteSaved?.(notesDraft.trim());
      await onRefresh?.();
    } finally {
      setNotesSaving(false);
    }
  };

  // "View SSL Per Diem Info" (v1 tablesearch link): open the Steamship Line Per
  // Diem tablemaint in a new tab, pre-filtered to this order's SCAC / size /
  // equip type via the sslperdiem cfg.filterParams column deep-links. Only
  // offered when the order carries a SCAC to look up.
  const orderScac = s(d.scac).trim();
  const openSslPerDiem = () => {
    if (!orderScac) return;
    const params = new URLSearchParams({ scac: orderScac });
    if (s(d.size).trim()) params.set('size', s(d.size).trim());
    if (s(d.equipType).trim()) params.set('equiptype', s(d.equipType).trim());
    window.open(`/data-management/ssl-per-diem?${params.toString()}`, '_blank', 'noopener');
  };
  const driverPayItems: ReadonlyArray<AddlCostDriverPayItem> = d.addlCostDriverPayItems ?? [];
  // Hide leg-derived pay lines that aren't actionable yet — a blank driver or
  // blank pay status means the leg isn't assigned/decided, so it's noise here.
  const legPayItems = driverPayItems.filter(
    (r) => r.type === 'LEG' && (r.driver ?? '').trim() !== '' && (r.payStatus ?? '').trim() !== '',
  );
  const otherPayItems = driverPayItems.filter((r) => r.type !== 'LEG');
  // Sum displayed net amounts in cents so the total matches the visible rows.
  const sumNetPay = (items: ReadonlyArray<AddlCostDriverPayItem>) => items.reduce((total, row) => {
    const { net } = gwiCommissionFor(row.rate, row.gwiCommission, row.fuelAmount);
    return total + (net == null ? 0 : Math.round(Number(net.toFixed(2)) * 100));
  }, 0) / 100;
  const legNetPay = sumNetPay(legPayItems);
  const additionalNetPay = sumNetPay(otherPayItems);
  const totalNetPay = legNetPay + additionalNetPay;
  // Editing requires a numeric order pronumber. Template pronumbers arrive as
  // "T…" strings and stay read-only (no template addl-cost / driver-pay rows).
  const numericPronumber = typeof pronumber === 'number' ? pronumber : null;
  const canEdit = numericPronumber != null && onRefresh != null;
  const [modalState, setModalState] = useState<{ isOpen: boolean; recno: number | null }>({
    isOpen: false,
    recno: null,
  });
  const openNew = () => setModalState({ isOpen: true, recno: null });
  const openEdit = (recno: number) => setModalState({ isOpen: true, recno });
  const closeModal = () => setModalState({ isOpen: false, recno: null });
  const [costModal, setCostModal] = useState<{ isOpen: boolean; recno: number | null }>({
    isOpen: false,
    recno: null,
  });
  const openCostNew = () => setCostModal({ isOpen: true, recno: null });
  const openCostEdit = (recno: number) => setCostModal({ isOpen: true, recno });
  const closeCostModal = () => setCostModal({ isOpen: false, recno: null });
  const addlItems: ReadonlyArray<AddlCostItem> = d.addlCostItems ?? [];
  const totalGwi = d.addlCostTotalGwiPayment ?? 0;
  const rejectedPaperwork: ReadonlyArray<RejectedDriverPaperworkItem> =
    d.rejectedDriverPaperwork ?? [];

  // Brokerage carrier block — only for brokerage dispatchers. Read-only for now.
  const isBrokerage = BROKERAGE_DISPATCHERS.has((d.dispatcher ?? '').trim().toLowerCase());
  const carrierRate = d.carrierRate ?? 0;
  const carrierFsc = d.carrierFsc ?? 0;
  const carrierAccessorial = d.carrierAccessorialRate ?? 0;
  const carrierAccessorialComment = d.carrierAccessorialComment ?? '';
  const fscTotal = (carrierRate * carrierFsc) / 100;
  const carrierTotal = carrierRate + fscTotal + carrierAccessorial;

  return (
    <div className="space-y-3">
      <div role="region" aria-label="Per Diem and Driver Pay" tabIndex={0} className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-x-auto">
        <div className="grid grid-cols-[minmax(max-content,1fr)] lg:grid-cols-[540px_minmax(max-content,1fr)] [&>section+section]:border-l-0 [&>section]:border-slate-100 dark:[&>section]:border-gray-700">
        {/* COLUMN 1 — Per Diem / Chassis Fee / Driver Paperwork */}
        <Section title="Per Diem & Chassis" icon={<Icon.calendar className="w-3.5 h-3.5" />}>
          {(() => {
            // Whole-row red (v1 solid red bar) only while the clock is actually
            // accruing — enddays - startdays > 0, where enddays falls back to the
            // terminate/delivery leg arrival (then today) when no perdiemenddate.
            const days = perDiemDaysAccrued(
              fv('perDiemStartDate'),
              fv('perDiemEndDate'),
              d.perDiemFallbackEnd,
            );
            const pastDue = days != null;
            const pdDate = (key: string, aria: string) =>
              perDiemEditable ? (
                <div style={{ width: 150 }}>
                  <DateTimePicker
                    mode="date"
                    inputVariant="compact"
                    selectionType="single"
                    autoApply
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder={aria}
                    ariaLabel={aria}
                    className="w-full [&_div]:!min-w-0"
                    value={parsePerDiemDate(fv(key))}
                    onChange={(v) => upd?.(key, formatMdYDate(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue
                  value={fv(key)}
                  mono
                  width={88}
                  ariaLabel={aria}
                  tone={pastDue ? 'white' : undefined}
                />
              );
            return (
              <Row className={pastDue ? 'bg-brand-red rounded px-1' : ''}>
                <Label width={LABEL_W} className={pastDue ? '!text-white' : ''}>
                  Per Diem Start / End
                </Label>
                {perDiemEditable && <BulkFieldCheckbox fieldName="perDiemStartDate" />}
                {pdDate('perDiemStartDate', 'Per Diem Start')}
                {perDiemEditable && <BulkFieldCheckbox fieldName="perDiemEndDate" />}
                {pdDate('perDiemEndDate', 'Per Diem End')}
                {days != null && (
                  <span
                    className={`inline-flex items-center h-6 px-1.5 text-[0.75rem] font-semibold whitespace-nowrap ${
                      pastDue ? 'text-white' : 'text-slate-700 dark:text-gray-300'
                    }`}
                    aria-label="Per Diem days accrued"
                  >
                    {days} {days === 1 ? 'day' : 'days'}
                  </span>
                )}
              </Row>
            );
          })()}
          <Row>
            <Label width={LABEL_W}>Gnosis Per Diem LFD</Label>
            <ReadValue value={d.perDiemSslInvRcvDate} mono ariaLabel="Gnosis Per Diem LFD" />
          </Row>
          <Row>
            <Label width={LABEL_W}>Days Free / Type</Label>
            <ReadValue
              value={d.perDiemDaysFree ? String(d.perDiemDaysFree) : undefined}
              width={48}
              ariaLabel="Per Diem Days Free"
            />
            <ReadValue value={d.perDiemDaysType} ariaLabel="Per Diem Days Type" />
            {orderScac && (
              <button
                type="button"
                onClick={openSslPerDiem}
                className="p-0.5 shrink-0 text-slate-500 hover:text-blue-600 cursor-pointer transition-colors"
                title="View SSL Per Diem Info"
                aria-label="View SSL Per Diem Info"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
              </button>
            )}
          </Row>
          <Row>
            <Label width={LABEL_W}>Source / Contract</Label>
            <ReadValue
              value={d.perDiemDaysSource}
              width={88}
              ariaLabel="Per Diem Days Source"
            />
            {perDiemEditable && <BulkFieldCheckbox fieldName="perDiemContractNumber" />}
            {perDiemEditable ? (
              <TextInput
                value={s(fv('perDiemContractNumber'))}
                onChange={(v) => upd?.('perDiemContractNumber', v)}
                mono
                ariaLabel="Per Diem Contract Number"
                placeholder="Contract"
              />
            ) : (
              <ReadValue
                value={d.perDiemContractNumber}
                mono
                ariaLabel="Per Diem Contract Number"
              />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W}>PD Amt / Responsible</Label>
            <ReadValue
              value={d.perDiemAmount ? money(d.perDiemAmount) : undefined}
              mono
              width={72}
              ariaLabel="Per Diem Amount"
            />
            {perDiemEditable && <BulkFieldCheckbox fieldName="perDiemApproval" />}
            {perDiemEditable ? (
              <SelectInput
                value={s(fv('perDiemApproval'))}
                options={PERDIEM_APPROVAL_OPTIONS}
                onChange={handleResponsibleChange}
                ariaLabel="Per Diem Responsible"
              />
            ) : (
              <ReadValue value={d.perDiemApproval} ariaLabel="Per Diem Responsible" />
            )}
            {perDiemEditable && (
              <button
                type="button"
                onClick={() => openNotes('pencil')}
                className="p-0.5 shrink-0 text-slate-500 hover:text-blue-600 cursor-pointer transition-colors"
                title="Edit per diem responsibility note"
                aria-label="Edit per diem responsibility note"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            )}
          </Row>
          <Row>
            <Label width={LABEL_W}>Per Diem Follow Up</Label>
            {/* Wired like perDiemApproval above. The REGISTRY decides whether this
                draws — `bulkFieldName` only says the field is capable of being fanned,
                and is_change_all says whether it currently is. Without the prop there
                is nothing for the flag to switch on, which is what made turning this
                field on in Data Management appear to do nothing. */}
            {perDiemEditable && <BulkFieldCheckbox fieldName="perDiemFollowupDate" />}
            {perDiemEditable ? (
              <div style={{ width: 150 }}>
                <DateTimePicker
                  mode="date"
                  inputVariant="compact"
                  selectionType="single"
                  autoApply
                  usePortal
                  openDirection="down"
                  autoFlip
                  labelType="none"
                  placeholder="Follow Up Date"
                  ariaLabel="Per Diem Follow Up"
                  className="w-full [&_div]:!min-w-0"
                  value={parsePerDiemDate(fv('perDiemFollowupDate'))}
                  onChange={(v) => upd?.('perDiemFollowupDate', formatMdYDate(v as Date | null))}
                />
              </div>
            ) : (
              <ReadValue value={d.perDiemFollowupDate} mono ariaLabel="Per Diem Follow Up" />
            )}
          </Row>

          <Divider />

          <Row>
            <Label width={LABEL_W}>Chassis Usage</Label>
            <ReadValue value={d.chassisUsage || undefined} mono ariaLabel="Chassis Usage" />
          </Row>
          <Row>
            <Label width={LABEL_W}>Chassis Days</Label>
            <ReadValue
              value={d.numChassisDays ? String(d.numChassisDays) : undefined}
              width={48}
              ariaLabel="Chassis Days"
            />
          </Row>
          <Row>
            <Label width={LABEL_W}>Calc Chassis Fee</Label>
            <ReadValue
              value={d.chassisFeeAmount ? money(d.chassisFeeAmount) : undefined}
              mono
              // V1 parity: red when a fee is calculated but no CHASSIS MANAGEMENT
              // invoice line exists to bill it (chassisfeeamount>0 && !haschassisfee).
              tone={(d.chassisFeeAmount ?? 0) > 0 && !d.hasChassisFee ? 'red' : undefined}
              ariaLabel="Calculated Chassis Fee"
            />
          </Row>
          {(() => {
            // V1 parity: Resolved (+ its comment) is read-only unless there's a
            // fee/billing mismatch to resolve — editable only when a fee is
            // calculated but unbilled, or billed with no calc.
            const feeAmt = Number(fv('chassisFeeAmount') ?? 0) || 0;
            const resolvedReadonly =
              (feeAmt <= 0 && !d.hasChassisFee) || (feeAmt > 0 && !!d.hasChassisFee);
            const resolvedEditable = perDiemEditable && !resolvedReadonly;
            return (
              <Row>
                <Label width={LABEL_W}>Resolved / Comment</Label>
                {resolvedEditable && <BulkFieldCheckbox fieldName="chassisFeeResolved" />}
                {resolvedEditable ? (
                  <SelectInput
                    value={s(fv('chassisFeeResolved'))}
                    options={['', 'Y', 'N']}
                    width={56}
                    onChange={(v) => upd?.('chassisFeeResolved', v)}
                    ariaLabel="Chassis Fee Resolved"
                  />
                ) : (
                  <ReadValue value={d.chassisFeeResolved} width={32} ariaLabel="Chassis Fee Resolved" />
                )}
                {resolvedEditable && <BulkFieldCheckbox fieldName="chassisFeeResolvedComment" />}
                {resolvedEditable ? (
                  <TextInput
                    value={s(fv('chassisFeeResolvedComment'))}
                    onChange={(v) => upd?.('chassisFeeResolvedComment', v)}
                    ariaLabel="Chassis Fee Comment"
                    placeholder="Comment"
                  />
                ) : (
                  <ReadValue value={d.chassisFeeResolvedComment} ariaLabel="Chassis Fee Comment" />
                )}
              </Row>
            );
          })()}

          <Divider />

          <Row className="!items-start">
            <Label width={LABEL_W} className="mt-1">
              Rejected Driver Paperwork
            </Label>
            <div
              className="flex-1 min-w-0 border border-slate-200 dark:border-gray-700 rounded bg-slate-50 dark:bg-gray-800 max-h-[120px] overflow-y-auto"
              aria-label="Rejected Driver Paperwork"
            >
              {rejectedPaperwork.length === 0 ? (
                <div className="px-1.5 py-1 text-[0.75rem] text-slate-400 dark:text-gray-500 italic">
                  {PLACEHOLDER}
                </div>
              ) : (
                <table className="w-full text-[0.71875rem]">
                  <thead className="bg-slate-100 dark:bg-gray-700">
                    <tr>
                      <TH className="text-left">Driver</TH>
                      <TH className="text-left">Type</TH>
                      <TH className="text-right">Rejected</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedPaperwork.map((row) => (
                      <tr key={row.recno}>
                        <TD>{row.createdBy || PLACEHOLDER}</TD>
                        <TD>{formatInfoType(row.infoType) || PLACEHOLDER}</TD>
                        <TD className="text-right">
                          {row.rejectedDatetime || PLACEHOLDER}
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Row>
        </Section>

        {/* COLUMN 2 — Driver Pay */}
        <Section title="Driver Pay" icon={<Icon.pkg className="w-3.5 h-3.5" />}>
          <div className="mb-2 text-right text-[0.75rem] text-slate-900 dark:text-gray-100">
            <span className="font-semibold">Total Net Pay:</span>{' '}
            <span aria-label="Total Net Pay" className="tabular-nums font-semibold">
              {money(totalNetPay)}
            </span>
          </div>
          <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
            Legs
          </div>
          <div className="border border-slate-200 dark:border-gray-700 rounded">
            <table
              className="w-full text-[0.75rem] table-auto"
              aria-label="Additional Driver Pay Items - Legs"
            >
              <thead className="bg-slate-50 dark:bg-gray-800">
                <tr>
                  <TH>Date</TH>
                  <TH>Driver</TH>
                  <TH>Leg</TH>
                  <TH />
                  <TH>Origin</TH>
                  <TH />
                  <TH>Destination</TH>
                  <TH>Fuel Type</TH>
                  <TH className="text-right">Rate</TH>
                  <TH className="text-right">Comm</TH>
                  <TH className="text-right">Fuel Amount</TH>
                  <TH className="text-right">Net</TH>
                  <TH className="text-center">Pay Status</TH>
                  <TH className="text-right">Payroll Date</TH>
                </tr>
              </thead>
              <tbody>
                {legPayItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={14}
                      className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                    >
                      No items
                    </td>
                  </tr>
                ) : (
                  legPayItems.map((row) => {
                    const { commission, net } = gwiCommissionFor(row.rate, row.gwiCommission, row.fuelAmount);
                    return (
                    <tr key={row.recno}>
                      <TD>{row.eventDate.slice(0, 5) || PLACEHOLDER}</TD>
                      <TD>{row.driver || PLACEHOLDER}</TD>
                      <TD>{row.legnum ?? PLACEHOLDER}</TD>
                      <TD>
                        <LegTypeChevron value={row.legType} ariaLabel="Leg type" />
                      </TD>
                      <TD>{row.origName || PLACEHOLDER}</TD>
                      <TD>
                        <LegTypeChevron value={row.legAction2} ariaLabel="Leg action" />
                      </TD>
                      <TD>{row.destName || PLACEHOLDER}</TD>
                      <TD>{row.fuelType || PLACEHOLDER}</TD>
                      <TD className="text-right">
                        {row.rate != null ? money(row.rate) : PLACEHOLDER}
                      </TD>
                      <TD className="text-right text-red-600 dark:text-red-300">
                        {commission != null ? `-${money(commission)}` : PLACEHOLDER}
                      </TD>
                      <TD className="text-right">
                        {row.fuelAmount != null ? money(row.fuelAmount) : PLACEHOLDER}
                      </TD>
                      <TD className="text-right">{net != null ? money(net) : PLACEHOLDER}</TD>
                      <TD className="text-center">{row.payStatus || PLACEHOLDER}</TD>
                      <TD className="text-right">
                        {row.payrollDate || PLACEHOLDER}
                      </TD>
                    </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-slate-50 dark:bg-gray-800 font-semibold">
                <tr>
                  <th scope="row" colSpan={11} className="px-2 py-1 text-right">Total Net Leg Pay:</th>
                  <TD className="text-right tabular-nums">
                    <span aria-label="Total Net Leg Pay">{money(legNetPay)}</span>
                  </TD>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="h-3" />

          <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
            Additional Driver Pay Items
          </div>
          <div className="border border-slate-200 dark:border-gray-700 rounded">
            <table
              className="w-full text-[0.75rem] table-auto"
              aria-label="Additional Driver Pay Items - Other"
            >
              <thead className="bg-slate-50 dark:bg-gray-800">
                <tr>
                  <TH>Date</TH>
                  <TH>Driver</TH>
                  <TH>Type</TH>
                  <TH className="text-right">Rate</TH>
                  <TH className="text-right">Comm</TH>
                  <TH className="text-right">Fuel Amount</TH>
                  <TH className="text-right">Net</TH>
                  <TH className="text-center">Pay Status</TH>
                  <TH className="text-right">Payroll Date</TH>
                </tr>
              </thead>
              <tbody>
                {otherPayItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                    >
                      No items
                    </td>
                  </tr>
                ) : (
                  otherPayItems.map((row) => {
                    const { commission, net } = gwiCommissionFor(row.rate, row.gwiCommission, row.fuelAmount);
                    return (
                    <tr
                      key={row.recno}
                      onClick={canEdit ? () => openEdit(row.recno) : undefined}
                      className={canEdit ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30' : ''}
                      data-testid={`addl-cost-other-row-${row.recno}`}
                    >
                      <TD>{row.eventDate || PLACEHOLDER}</TD>
                      <TD>{row.driver || PLACEHOLDER}</TD>
                      <TD>
                        {row.type || PLACEHOLDER}
                        {row.lineItemPro && (
                          <span className="text-slate-500 dark:text-gray-400"> ({row.lineItemPro})</span>
                        )}
                        {row.isPaid && (
                          <span
                            data-testid="addl-cost-paid-badge"
                            aria-label="Paid"
                            title="On an invoiced payroll — locked from edits and deletes"
                            className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-slate-200 dark:bg-gray-700 px-1 py-px text-[0.625rem] font-semibold uppercase tracking-wide text-slate-600 dark:text-gray-300 align-middle"
                          >
                            <LockClosedIcon className="w-2.5 h-2.5" aria-hidden="true" /> Paid
                          </span>
                        )}
                      </TD>
                      <TD className="text-right">
                        {isMissingBobtailRate(row) ? (
                          <span
                            data-testid={`addl-cost-missing-rate-${row.recno}`}
                            title={MISSING_RATE_TITLE}
                            className="inline-flex items-center rounded bg-amber-100 dark:bg-amber-900/40 px-1 py-px text-[0.625rem] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300"
                          >
                            Missing Rate
                          </span>
                        ) : (
                          row.rate != null ? money(row.rate) : PLACEHOLDER
                        )}
                      </TD>
                      <TD className="text-right text-red-600 dark:text-red-300">
                        {commission != null ? `-${money(commission)}` : PLACEHOLDER}
                      </TD>
                      <TD className="text-right">
                        {row.fuelAmount != null ? money(row.fuelAmount) : PLACEHOLDER}
                      </TD>
                      <TD className="text-right">{net != null ? money(net) : PLACEHOLDER}</TD>
                      <TD className="text-center">{row.payStatus || PLACEHOLDER}</TD>
                      <TD className="text-right">
                        {row.payrollDate || PLACEHOLDER}
                      </TD>
                    </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-slate-50 dark:bg-gray-800 font-semibold">
                <tr>
                  <th scope="row" colSpan={6} className="px-2 py-1 text-right">Total Net Add Driver Pay:</th>
                  <TD className="text-right tabular-nums">
                    <span aria-label="Total Net Add Driver Pay">{money(additionalNetPay)}</span>
                  </TD>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
          {canEdit && (
            <PermissionsContainer
              componentId={DRIVER_PAY_PERMISSION_ID}
              componentName={DRIVER_PAY_PERMISSION_NAME}
              className="mt-2 flex justify-end"
            >
              {/* PermissionAwareButton: View (1) on the component renders the
                  section but must not allow adds — the container only hides at 0. */}
              <PermissionAwareButton
                type="button"
                onClick={openNew}
                title="Requires edit access to Driver Pay additions"
                className="px-2 py-0.5 text-[0.6875rem] font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-50 dark:disabled:hover:bg-blue-900/30"
                data-testid="addl-cost-add-addition"
              >
                + Add
              </PermissionAwareButton>
            </PermissionsContainer>
          )}
        </Section>
        </div>
      </div>

      {/* BROKERAGE CARD — carrier rate / FSC / accessorial. Only shown when the
          order's dispatcher is a brokerage dispatcher. Read-only for now, so
          values render as plain text; wrap each in an input when it becomes
          editable. */}
      {isBrokerage && (
        <div className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-x-auto">
          <Section title="Brokerage" icon={<Icon.truck className="w-3.5 h-3.5" />}>
            <div className="max-w-3xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <Row>
                  <Label width={148}>Carrier Rate</Label>
                  <ReadValue
                    value={carrierRate ? money(carrierRate) : undefined}
                    mono
                    width={128}
                    ariaLabel="Carrier Rate"
                  />
                </Row>
                <Row>
                  <Label width={148}>Carrier FSC</Label>
                  <ReadValue
                    value={carrierFsc ? `${carrierFsc.toFixed(2)} %` : undefined}
                    mono
                    width={128}
                    ariaLabel="Carrier FSC"
                  />
                </Row>
                <Row>
                  <Label width={148}>Carrier Accessorial</Label>
                  <ReadValue
                    value={carrierAccessorial ? money(carrierAccessorial) : undefined}
                    mono
                    width={128}
                    ariaLabel="Carrier Accessorial"
                  />
                </Row>
                <Row>
                  <Label width={148}>FSC Total</Label>
                  <ReadValue
                    value={fscTotal ? money(fscTotal) : undefined}
                    mono
                    width={128}
                    ariaLabel="FSC Total"
                  />
                </Row>
              </div>
              <Row className="!items-start">
                <Label width={148} className="mt-1">
                  Accessorial Comment
                </Label>
                <span
                  className={`flex-1 min-w-0 py-1 text-[0.75rem] whitespace-pre-wrap ${
                    carrierAccessorialComment
                      ? 'text-slate-900 dark:text-gray-100'
                      : 'text-slate-400 dark:text-gray-500'
                  }`}
                  aria-label="Accessorial Comment"
                >
                  {carrierAccessorialComment || PLACEHOLDER}
                </span>
              </Row>
              <div className="mt-1 flex items-center justify-end gap-2 border-t border-slate-200 dark:border-gray-700 pt-2">
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                  Total
                </span>
                <span
                  className="font-semibold text-[0.8125rem] text-slate-900 dark:text-gray-100 tabular-nums"
                  aria-label="Brokerage Total"
                >
                  {money(carrierTotal)}
                </span>
              </div>
            </div>
          </Section>
        </div>
      )}

      {/* SECOND CARD — Additional Cost. Separated by space-y-3 from the
          Per Diem / Driver Pay card above. */}
      <div className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-x-auto">
        <Section
          title="Additional Cost"
          icon={<Icon.ticket className="w-3.5 h-3.5" />}
        >
          <div className="flex items-baseline justify-end mb-1">
            <div className="text-[0.71875rem] text-slate-700 dark:text-gray-300">
              <span className="font-semibold">Total GWI Payment:</span>{' '}
              <span
                className={
                  totalGwi > 0
                    ? 'text-slate-900 dark:text-gray-100 font-semibold'
                    : 'text-slate-400 dark:text-gray-500'
                }
                aria-label="Total GWI Payment"
              >
                {totalGwi > 0 ? money(totalGwi) : PLACEHOLDER}
              </span>
            </div>
          </div>
          <div className="border border-slate-200 dark:border-gray-700 rounded overflow-x-auto">
            <table
              className="w-full text-[0.75rem] table-auto"
              aria-label="Additional Cost Items"
            >
              <thead className="bg-slate-50 dark:bg-gray-800">
                <tr>
                  <TH>Invoice #</TH>
                  <TH>Item Type</TH>
                  <TH className="text-right">Invoice Total</TH>
                  <TH className="text-right">GWI Payment</TH>
                </tr>
              </thead>
              <tbody>
                {addlItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                    >
                      No items
                    </td>
                  </tr>
                ) : (
                  addlItems.map((row) => (
                    <tr
                      key={row.recno}
                      onClick={canEdit ? () => openCostEdit(row.recno) : undefined}
                      className={canEdit ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30' : ''}
                      data-testid={`addl-cost-item-row-${row.recno}`}
                    >
                      <TD>{row.invoiceNumber || 'NONE'}</TD>
                      <TD>{row.costType || PLACEHOLDER}</TD>
                      <TD className="text-right">{money(row.invoiceTotal)}</TD>
                      <TD className="text-right">{money(row.gwiPayment)}</TD>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <PermissionsContainer
              componentId={COST_ITEM_PERMISSION_ID}
              componentName={COST_ITEM_PERMISSION_NAME}
              className="mt-2 flex justify-end"
            >
              <PermissionAwareButton
                type="button"
                onClick={openCostNew}
                title="Requires edit access to Additional Cost items"
                className="px-2 py-0.5 text-[0.6875rem] font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-50 dark:disabled:hover:bg-blue-900/30"
                data-testid="addl-cost-add-cost-item"
              >
                + Add
              </PermissionAwareButton>
            </PermissionsContainer>
          )}
        </Section>
      </div>
      {canEdit && (
        <>
          <DriverPayAdditionModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            pronumber={numericPronumber!}
            recno={modalState.recno}
            onSaved={() => onRefresh!()}
            existingAdditions={otherPayItems.map((r) => ({ type: r.type, rate: r.rate ?? 0, recno: r.recno }))}
          />
          <AdditionalCostItemModal
            isOpen={costModal.isOpen}
            onClose={closeCostModal}
            pronumber={numericPronumber!}
            recno={costModal.recno}
            onSaved={() => onRefresh!()}
          />
        </>
      )}
      {notesMode !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Per Diem Responsibility"
          onClick={cancelNotes}
        >
          <div
            ref={notesPanelRef}
            style={notesPanelStyle}
            className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-slate-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title="Per Diem Responsibility"
              onClose={cancelNotes}
              dragHandleProps={notesDragHandleProps}
            />
            <div className="p-4">
              <label className="block text-[0.6875rem] font-semibold text-slate-600 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={8}
                autoFocus
                aria-label="Per Diem Responsibility Notes"
                className="w-full text-[0.78125rem] rounded border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 resize-y text-slate-900 dark:text-gray-100"
              />
            </div>
            <div className="px-4 py-2.5 border-t border-slate-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelNotes}
                disabled={notesSaving}
                className="px-3 py-1 text-xs font-semibold rounded border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNotes}
                disabled={notesSaving}
                className="px-3 py-1 text-xs font-semibold rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {notesSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddlCostView;
